import { useEffect } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { apps } from "@/lib/apps";
import { EASE } from "@/components/motion";

const orbit = [
  { pos: "left-[40%] -top-[12%]", z: 140, size: "w-[21%]", dur: 6 },
  { pos: "-right-[4%] top-[22%]", z: 90, size: "w-[18%]", dur: 7 },
  { pos: "-left-[2%] bottom-[8%]", z: 70, size: "w-[17%]", dur: 8 },
  { pos: "right-[2%] -bottom-[4%]", z: 170, size: "w-[24%]", dur: 6.5 },
];

export const HeroEmblem = ({ delay }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const cfg = { stiffness: 80, damping: 18 };
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [14, -14]), cfg);
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-20, 20]), cfg);
  const sheenX = useSpring(useTransform(mx, [-0.5, 0.5], ["-30%", "130%"]), cfg);

  useEffect(() => {
    const move = (e) => {
      mx.set(e.clientX / window.innerWidth - 0.5);
      my.set(e.clientY / window.innerHeight - 0.5);
    };
    window.addEventListener("pointermove", move);
    return () => window.removeEventListener("pointermove", move);
  }, [mx, my]);

  return (
    <motion.div
      data-testid="hero-emblem"
      initial={{ opacity: 0, scale: 0.8, y: 60 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 1.6, ease: EASE, delay }}
      className="relative mx-auto aspect-square w-[74vw] max-w-[480px]"
      style={{ perspective: 1100 }}
    >
      <motion.div className="relative h-full w-full" style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}>
        <div className="emblem-glow absolute inset-[4%] rounded-[30%]" style={{ transform: "translateZ(-90px)" }} />
        <div className="absolute inset-[8%] overflow-hidden rounded-[27%] border border-white/15 shadow-[0_50px_120px_-30px_rgba(34,211,238,0.55)]" style={{ transform: "translateZ(30px)" }}>
          <img src="/logos/zenvio.webp" alt="ZenVio logo" className="h-full w-full scale-[1.13] object-cover" />
          <motion.div className="pointer-events-none absolute inset-y-0 w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/15 to-transparent" style={{ left: sheenX }} />
        </div>
        {apps.map((app, i) => (
          <div key={app.slug} className={`absolute ${orbit[i].pos} ${orbit[i].size}`} style={{ transform: `translateZ(${orbit[i].z}px)` }}>
            <motion.div
              animate={{ y: [0, -12, 0] }}
              transition={{ duration: orbit[i].dur, repeat: Infinity, ease: "easeInOut" }}
              className="aspect-square overflow-hidden rounded-[26%] border border-white/20 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.8)]"
            >
              <img src={app.logo} alt={`${app.name} icon`} className="h-full w-full object-cover" style={{ transform: `scale(${app.logoScale + 0.08})` }} />
            </motion.div>
          </div>
        ))}
      </motion.div>
    </motion.div>
  );
};
